import moment from 'moment';
import { flatMap, max, min } from 'lodash';
import { SeriesOptionsType } from 'highcharts';

import { formatMessageWithLocale } from '@/utils/i18n';
import Locale from '@/types/locale';

type SeriesData = [number, number][];

/**
 * Formats a timestamp to a localized date string
 * @param timestamp - Unix timestamp
 * @param locale - User locale for date formatting
 * @returns Formatted date string
 */
const formatTimestampToDate = (timestamp: number, locale?: Locale): string =>
  moment.utc(new Date(timestamp)).format(formatMessageWithLocale('Common.DateFormats.Short', locale));

/**
 * Type guard to check if a series has data property with array of data points
 */
const hasSeriesData = (serie: SeriesOptionsType): serie is SeriesOptionsType & { data: number[][] } =>
  serie && 'data' in serie && Array.isArray(serie.data) && serie.data.length > 0;

/**
 * Gets the latest date from series data for disclaimers
 * Finds the maximum timestamp across all data points in the series
 * @param series - Array of [timestamp, value] arrays
 * @param locale - User locale for date formatting
 * @returns Formatted date string of the latest timestamp found
 */
export const getLatestGraphedDate = (series: number[][], locale?: Locale) => {
  const timestamp = series.reduce((date, data) => {
    if (!data || data.length === 0) {
      return date;
    }

    return Math.max(date, data[0]);
  }, -Infinity);

  if (timestamp === -Infinity) {
    return '';
  }

  return moment.utc(new Date(timestamp)).format(formatMessageWithLocale('Common.DateFormats.Short', locale));
};

/**
 * Gets the latest date from Highcharts series data for disclaimers
 * Finds the maximum timestamp across all data points in the series
 * @param seriesData - Array of Highcharts series options
 * @param locale - User locale for date formatting
 * @returns Formatted date string of the latest timestamp found
 */
export const getLatestGraphedDateFromSeries = (seriesData: SeriesOptionsType[], locale?: Locale): string => {
  // Extract all data points from all series
  const allDataPoints: number[][] = [];

  for (const serie of seriesData) {
    if (hasSeriesData(serie) && serie.data.length > 0) {
      allDataPoints.push(...serie.data);
    }
  }

  if (allDataPoints.length === 0) {
    return '';
  }

  return getLatestGraphedDate(allDataPoints, locale);
};

export const getEarliestAndLatestDatesFromSeries = (seriesData: SeriesOptionsType[]): { startDate: Date | null; endDate: Date | null } => {
  const allTimestamps = flatMap(seriesData, serie =>
    'data' in serie && Array.isArray(serie.data) ? (serie.data as SeriesData).map(([timestamp]) => timestamp) : [],
  );

  const minTimeStamp = min(allTimestamps);
  const maxTimeStamp = max(allTimestamps);

  return {
    startDate: minTimeStamp ? new Date(minTimeStamp) : null,
    endDate: maxTimeStamp ? new Date(maxTimeStamp) : null,
  };
};
